import { resolveRouteIn } from '@beautyburst/routing';
import type { AppRouteContract, RouteMatch } from '@beautyburst/routing';
import { retailerBasePath } from './base-path.ts';
import type { AppBasePath } from '@beautyburst/tokens';

export type RetailerNavKey =
  | 'dashboard'
  | 'orders'
  | 'catalog'
  | 'inventory'
  | 'imports'
  | 'store'
  | 'after-hours'
  | 'payouts'
  | 'security'
  | null;

/**
 * A route remains explicit about its client gate as well as its server
 * authorization requirement. The API is the authorization boundary; this table
 * keeps unavailable or unauthorized controls out of the retailer experience.
 */
export interface RouteContract extends AppRouteContract {
  flag: string;
  navKey: RetailerNavKey;
  phase: number;
  /**
   * An operational endpoint and compatible portal screen both exist. Routes
   * that are not ready remain addressable only to show an honest status, never
   * as navigation dead ends.
   */
  available: boolean;
}

export const RETAILER_ROUTES: readonly RouteContract[] = [
  {
    path: '/sign-in',
    screen: 'Sign in',
    permission: null,
    flag: 'retailer.portal_auth',
    navKey: null,
    phase: 7,
    available: true,
  },
  {
    path: '/partner',
    screen: 'Retailer partnership',
    permission: null,
    flag: 'retailer.lead_capture',
    navKey: null,
    phase: 3,
    available: true,
  },
  {
    path: '/',
    screen: 'Dashboard',
    permission: 'retailer.dashboard.read',
    flag: 'retailer.portal',
    navKey: 'dashboard',
    phase: 7,
    available: true,
  },
  {
    path: '/security',
    screen: 'Security',
    permission: 'retailer.dashboard.read',
    flag: 'retailer.portal',
    navKey: 'security',
    phase: 7,
    available: true,
  },
  {
    path: '/orders',
    screen: 'Orders',
    permission: 'retailer.order.read',
    flag: 'retailer.fulfilment',
    navKey: 'orders',
    phase: 7,
    available: true,
  },
  {
    path: '/orders/:fulfilmentId',
    screen: 'Order detail',
    permission: 'retailer.order.read',
    flag: 'retailer.fulfilment',
    navKey: 'orders',
    phase: 7,
    available: true,
  },
  {
    path: '/catalog',
    screen: 'Catalog',
    permission: 'retailer.catalog.read',
    flag: 'retailer.catalog_management',
    navKey: 'catalog',
    phase: 7,
    available: true,
  },
  {
    path: '/inventory',
    screen: 'Inventory',
    permission: 'retailer.inventory.read',
    flag: 'retailer.inventory_management',
    navKey: 'inventory',
    phase: 7,
    available: true,
  },
  {
    path: '/imports',
    screen: 'Imports',
    permission: 'retailer.import.manage',
    flag: 'retailer.bulk_import',
    navKey: 'imports',
    phase: 7,
    available: true,
  },
  {
    path: '/store',
    screen: 'Store profile',
    permission: 'retailer.settings.manage',
    flag: 'retailer.store_profile',
    // navKey must match this route's own path segment; it was previously
    // swapped with the Beauty After Hours entry, so the active-nav highlight
    // pointed at the wrong link on both screens.
    navKey: 'store',
    phase: 7,
    available: true,
  },
  {
    path: '/after-hours',
    screen: 'Beauty After Hours',
    permission: 'retailer.overnight.manage',
    flag: 'retailer.overnight_program',
    navKey: 'after-hours',
    phase: 7,
    available: true,
  },
  {
    path: '/payouts',
    screen: 'Payouts',
    permission: 'retailer.payout.read',
    flag: 'retailer.payouts',
    navKey: 'payouts',
    phase: 7,
    available: true,
  },
] as const;

export function resolveRoute(
  pathname: string,
  basePath: AppBasePath = retailerBasePath,
): RouteMatch<RouteContract> | null {
  return resolveRouteIn(RETAILER_ROUTES, basePath.strip(pathname));
}

export type { RouteMatch };
